<template>
    <div id="room-chat" class="tw-flex tw-flex-col tw-h-full">
        <!-- Header -->
        <div id="chat-header">
            <h4 class="tw-text-center tw-mb-4 title">Chat</h4>
            <hr class="border divider-color"/>
        </div>

        <!-- Main -->
        <div id="chat-messages" ref="messages"
             class="tw-flex tw-flex-1 tw-flex-col-reverse tw-items-end tw-overflow-auto">
            <div class="tw-flex tw-flex-col-reverse tw-w-full message-list" @scroll="isTopOfList">
                <template v-for="message in messages">
                    <chat-message
                        :ref="`message-${message.id}`"
                        :message="message"
                        :thread="thread"
                        :hosts="hosts"
                        @resendMessage="resendMessage"
                        :livekit_local_participant="livekit_local_participant"/>
                </template>
            </div>
        </div>

        <!-- Footer -->
        <div id="chat-textfield">
            <hr class="border divider-color"/>
            <huddle-editor-v2
                ref="editorFroala"
                v-model="new_message"
                :initializing="loading"
                :submit_on_enter="true"
                :prevent_submit="loading"
                :placeholder="$t('messages.type_message')"
                :mobile_send_btn="true"
                :multi_line="true"
                :submittable="true"
                :disable_spinner="true"
                @submit="sendMessage(new_message)"

                :gifs="false"
                :images="false"
                :mentions="false"
                :emojis="false">
            </huddle-editor-v2>
        </div>
    </div>
</template>

<script>
import {debounce} from 'lodash';
import ChatMessage from "./ChatMessage";
import Thread from "../../lib/models/Thread";
import HuddleEditorV2 from '../HuddleEditorV2.vue';

export default {
    name: "RoomChat",
    components: {
        'chat-message': ChatMessage,
        'huddle-editor-v2': HuddleEditorV2,
    },
    props: ['tEvent', 'live_room_participants', 'livekit_local_participant'],
    data() {
        return {
            new_message: '',
            pagination: {
                last_page: 1,
                current_page: 1,
            },
            loading: true,
            thread: null,
            latest_displayed_message: null,
        }
    },
    computed: {
        //  Mental note: this should automagically gets updated when Messages get fetched or the array changes.
        messages() {
            if (!this.thread) {
                return false
            }
            const messages = Message.query()
                .where('thread_id', this.thread.id)
                .orderBy('created_at', 'desc')
                .get();

            return messages;
        },
        user() {
            return window.user;
        },
        hosts() {
            const hosts = [];
            const participants = this.live_room_participants.filter(participant => participant.room_admin === 1);
            for (let i = 0; i < participants.length; i++) {

                hosts.push(participants[i].user_id);
            }
            return hosts;
        },
    },
    methods: {
        async fetchInitialMessages() {
            // Our goal is to fetch enough messages so that the latest displayed messages is not in the viewport (and the user can scroll through the list).
            await this.fetchMessages(this.pagination.current_page, 10);
            // Newest comes first from bottom to top. So think of this in reverse.
            this.latest_displayed_message = this.messages[this.messages.length - 1]
            while (this.pagination.current_page <= this.pagination.last_page && this.isInViewPort(this.latest_displayed_message)) {
                await this.fetchMessages(this.pagination.current_page + 1, 10);
                this.latest_displayed_message = this.messages[this.messages.length - 1];
            }
        },
        async fetchMessages(page, size) {
            this.loading = true;
            const response = await Message.api().fetchByThreadId(this.thread.id, page, size);
            this.pagination.last_page = response.response.data.meta.pagination.last_page;
            this.pagination.current_page = response.response.data.meta.pagination.current_page;
            this.loading = false;
        },
        isTopOfList() {
            if (this.pagination.current_page >= this.pagination.last_page) return;
            const div = this.$refs.messages;
            if (div) {
                // *0.99 to load just before user reaches the end of the list
                let top_of_list = (Math.abs(div.scrollTop) + div.clientHeight) >= (div.scrollHeight * 0.995);
                if (top_of_list) {
                    debounce(this.fetchMessages(this.pagination.current_page + 1), 250);
                }
            }
        },
        async getThread() {
            const response = await Thread.api().getThreadByChannelId(this.tEvent.channel_id, 'group');
            return response.response.data.data;
        },
        sendMessage(body) {
            const message = {
                message: body,
                name: self.user.first_name + ' ' + self.user.last_name,
                avatar_url: user.avatar,
            };

            Message.api().send(this.thread.id, message).catch((failed_message_id) => {
                Message.update({
                    where: failed_message_id,
                    data: {
                        error: true
                    }
                })
            });

            setTimeout(() => {
                this.focusInput();
            }, 0);
        },
        focusInput() {
            const editor = this.$refs.editorFroala.$refs.froala.getEditor();
            editor.el.focus();
        },
        async resendMessage(message) {
            await Thread.update({
                where: this.thread.id,
                data(thread) {
                    thread.sent_message_ids = thread.sent_message_ids.filter(id => id === message.id);
                },
            });

            await Message.delete(message.id);

            this.sendMessage(message.body);
        },
        async setupChannel(threadId) {
            window.Echo.private('chat.' + threadId)
                .listen('.inbox', async (payload) => {
                    if (payload.message.user_id === self.user.id) {
                        await this.replaceFakeMessage(this.thread.id);
                    } else {
                        Message.insert({
                            data: [
                                payload.message
                            ],
                        });
                    }
                });
        },
        async replaceFakeMessage(thread_id) {
            const thread = await Thread.find(thread_id)
            // Sent_message_ids holds the fake messages.
            if (thread.hasOwnProperty('sent_message_ids')) {
                const stored_fake_messages = [];
                for (const id of thread.sent_message_ids) {
                    const message = await Message.find(id);
                    if (message) {
                        stored_fake_messages.push(message)
                    }
                }
                for (const fake_message of stored_fake_messages) {
                    await Message.update({
                        where: fake_message.id,
                        data(found_message) {
                            found_message.new = false;
                            found_message.not_send = false;
                            found_message.fake = false;
                        }
                    })
                    // remove the updated message id from the thread message array
                    let new_sent_message_ids = [];
                    let index = thread.sent_message_ids.indexOf(fake_message.id);
                    if (index > -1) {
                        new_sent_message_ids = thread.sent_message_ids.splice(index, 1);
                    }
                    await Thread.update({
                        where: thread_id,
                        data(found_thread) {
                            found_thread.sent_message_ids = new_sent_message_ids;
                        }
                    });
                }
            }
        },
        isInViewPort(message) {
            if (!message) {
                return;
            }

            const element = this.$refs[`message-${message.$id}`][0].$el;
            const rect = element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <=
                (window.innerHeight ||
                    document.documentElement.clientHeight) &&
                rect.right <=
                (window.innerWidth ||
                    document.documentElement.clientWidth)
            );
        }
    },
    async created() {
        this.thread = await this.getThread();
        await this.fetchInitialMessages();
        await this.setupChannel(this.thread.id);
    },
    mounted() {
        this.$refs.editorFroala.initialize(true);
    },
}
</script>

<style lang='scss' scoped>
.message-list {
    gap: 16px;

    -ms-overflow-style: none; /* for Internet Explorer, Edge */
    scrollbar-width: none; /* for Firefox */
    overflow-y: scroll;
}
.message-list::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
}

#chat-textfield {
    overflow: auto;
    -ms-overflow-style: none; /* for Internet Explorer, Edge */
    scrollbar-width: none; /* for Firefox */
    overflow-y: scroll;
}

#chat-textfield::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */

}
</style>
